"use client";

import { useEffect, useMemo, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  DEFAULT_LOCALE,
  getLocaleFromPathname,
  localeLabels,
  locales,
  withLocale,
} from "@/lib/i18n";
import { getDictionary } from "@/lib/messages";

const STORAGE_KEY = "dogufy-locale";
const COOKIE_KEY = "dogufy-locale";

export function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const currentLocale = getLocaleFromPathname(pathname);
  const dict = getDictionary(currentLocale);

  const normalizedSearch = useMemo(() => {
    const query = searchParams?.toString();
    return query ? `?${query}` : "";
  }, [searchParams]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, currentLocale);
    document.cookie = `${COOKIE_KEY}=${currentLocale}; path=/; max-age=31536000; SameSite=Lax`;
  }, [currentLocale]);

  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">{dict.nav.language}</span>
      <select
        value={currentLocale}
        disabled={isPending}
        onChange={(event) => {
          const nextLocale = event.target.value || DEFAULT_LOCALE;
          window.localStorage.setItem(STORAGE_KEY, nextLocale);
          document.cookie = `${COOKIE_KEY}=${nextLocale}; path=/; max-age=31536000; SameSite=Lax`;
          startTransition(() => {
            router.push(`${withLocale(nextLocale as typeof currentLocale, pathname)}${normalizedSearch}`);
          });
        }}
        className="h-12 rounded-[14px] border border-positivus-dark/20 bg-white px-4 text-sm font-medium text-black shadow-sm outline-none transition-colors hover:border-positivus-dark focus:border-positivus-dark"
      >
        {locales.map((locale) => (
          <option key={locale} value={locale}>
            {localeLabels[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
